const API_BASE = '/api';

interface ApiError extends Error {
  code?: string;
  status?: number;
}

interface ApiErrorPayload {
  error?: string;
  message?: string;
  code?: string;
}

function formatApiError(data: ApiErrorPayload, status: number): string {
  const base = data.error || data.message || `Ошибка запроса (${status})`;
  if (data.error && data.message && data.message !== data.error) {
    return `${data.error}: ${data.message}`;
  }
  return base;
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };

  const hasBody = options.body !== undefined && options.body !== null && options.body !== '';
  if (hasBody && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  let data: ApiErrorPayload & { data?: T; success?: boolean };
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      const error = new Error(`Ошибка запроса (${response.status})`) as ApiError;
      error.status = response.status;
      throw error;
    }
    throw new Error('Некорректный ответ сервера');
  }

  if (!response.ok) {
    const error = new Error(formatApiError(data, response.status)) as ApiError;
    error.code = data.code;
    error.status = response.status;
    throw error;
  }

  return data.data !== undefined ? data.data : (data as T);
}

export async function downloadFile(
  endpoint: string,
  body: unknown,
  fallbackFilename: string
): Promise<void> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body ?? {}),
  });

  if (!response.ok) {
    let message = `Ошибка запроса (${response.status})`;
    try {
      const data = (await response.json()) as ApiErrorPayload;
      message = formatApiError(data, response.status);
    } catch {
      const text = await response.text();
      if (text) message = text.slice(0, 500);
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') ?? '';
  const matched = disposition.match(/filename="?([^"]+)"?/i);
  const filename = matched?.[1] || fallbackFilename;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint),

  post: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),

  postText: <T>(endpoint: string, body: string, contentType: string) =>
    request<T>(endpoint, {
      method: 'POST',
      body,
      headers: { 'Content-Type': contentType },
    }),

  put: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body ?? {}),
    }),

  delete: <T>(endpoint: string) =>
    request<T>(endpoint, { method: 'DELETE' }),
};
